import {
  get, isNumber, isString, uniq, cloneDeep,
} from 'lodash';
import React from 'react';

import { CHARGER_NODE_TYPE, EDIT_SCHEMA_MODE, JUNCTION_NODE_TYPE } from '../../constants';
import { EVENT_PANEL_WIDTH } from '../../LogEvents/helper';
import ConnectionLine from '../DrawingContent/ConnectionLine';
import EnergyDirectionArrow from '../DrawingContent/EnergyDirectionArrow';
import RemoveConnectionTrigger from '../DrawingContent/RemoveConnectionTrigger';
import { isExternalSourceNode } from './node';

export const MENU_HEIGHT = 179;
export const SCHEMA_PADDING_LEFT = 20;
export const SCHEMA_PADDING_TOP = 40;
export const SCHEMA_PADDING_RIGHT = 10;
export const SCHEMA_PADDING_BOTTOM = 100;
export const COLUMN_WIDTH = 235;
export const COLUMN_GAP = 10;
export const ROW_HEIGHT = 200;
export const NODE_MARGIN_TOP = 100;

export const mapByKey = (items, key) => items.reduce((mappedItems, item) => {
  mappedItems[get(item, key)] = item;

  return mappedItems;
}, {});

export const mapRecursively = ({
  items,
  key = 'id',
  nestedKey = 'children',
  mapped = {},
  sources = {},
}) => {
  if (items.length) {
    items.forEach((item) => {
      if (item[key]) {
        mapped[item[key]] = cloneDeep(item);

        if (sources[item[key]]) {
          if (!mapped[item[key]][nestedKey]) mapped[item[key]][nestedKey] = [];
          mapped[item[key]][nestedKey] = mapped[item[key]][nestedKey].concat(
            sources[item[key]],
          );
        }

        if (mapped[item[key]][nestedKey]) {
          mapRecursively({
            items: mapped[item[key]][nestedKey], key, nestedKey, mapped, sources,
          });
        }
      }
    });
  }

  return mapped;
};

export const mapSourcesByJunction = (nodes) => nodes.reduce((acc, node) => {
  const junctionId = node.junctionNodeId || (node.staticSection ? node.staticSection.junctionNodeId : null);

  if (junctionId) {
    if (!acc[junctionId]) {
      acc[junctionId] = [];
    }

    acc[junctionId].push(node);
  }

  return acc;
}, {});

const appendNodeToHierarchy = (flatHierarchy, hierarchyItem, nodes, level, parent = null, sources) => {
  if (!flatHierarchy[level]) {
    flatHierarchy[level] = [];
  }

  const currentLevelContainer = flatHierarchy[level];
  const node = nodes[hierarchyItem.id];
  let children = [];

  if (node && sources[node.id]) {
    children = children.concat(sources[node.id].map((source) => source.id));
    sources[node.id].forEach(
      (child) => appendNodeToHierarchy(flatHierarchy, child, nodes, level + 1, hierarchyItem.id, sources),
    );
  }

  if (hierarchyItem.children && hierarchyItem.children.length) {
    children = children.concat(hierarchyItem.children.map((child) => child.id));
    hierarchyItem.children.forEach(
      (child) => appendNodeToHierarchy(flatHierarchy, child, nodes, level + 1, hierarchyItem.id, sources),
    );
  }

  currentLevelContainer.push({ node, children, parent });

  return flatHierarchy;
};

const moveThroughFlatHierarchyItems = (items, collectMethod) => items.map(
  (hierarchyItems, level) => hierarchyItems.map(
    (item, position) => {
      const prevLevelItems = items[level - 1];
      const nextLevelItems = items[level + 1];
      const parentItem = prevLevelItems ? prevLevelItems.find((prevItem) => prevItem.node && prevItem.node.id === item.parent) : null;

      return collectMethod({
        items,
        level,
        levelItems: hierarchyItems,
        prevLevelItems,
        nextLevelItems,
        neighborLeft: hierarchyItems[position - 1],
        neighborRight: hierarchyItems[position + 1],
        parentItem,
        item,
        position,
      });
    },
  ),
);

export const moveThroughHierarchy = (hierarchyItem, nodes, collectMethod) => {
  const { id, children } = hierarchyItem;
  const node = nodes[id];

  collectMethod({ node });

  if (children && children.length) {
    children.forEach(
      (child) => moveThroughHierarchy(child, nodes, collectMethod),
    );
  }
};

const calculateColumnFilling = (children = [], nodes, limit = 10) => {
  let count = 0;

  if (children && children.length) {
    if (children.length > limit) {
      return limit;
    }
    children.forEach(
      (child) => {
        const childId = isString(child) ? child : child.id;
        const node = nodes[childId];

        if (node) {
          count += calculateColumnFilling(node.children, nodes, limit);
        }
      },
    );
  }

  return count || 1;
};

const calculateHierarchyColumnFilling = (hierarchyItems, hierarchy, sources, limit = 10) => {
  let maxFilling = 0;

  const mappedHierarchy = mapRecursively({ items: [hierarchy], sources });

  moveThroughFlatHierarchyItems(
    hierarchyItems,
    ({ item, neighborLeft, parentItem }) => {
      if (item && item.children) {
        item.columnFilling = calculateColumnFilling(item.children, mappedHierarchy, limit);

        if (neighborLeft) {
          item.columnFillingLeft = neighborLeft.columnFilling + neighborLeft.columnFillingLeft;
        } else {
          item.columnFillingLeft = 0;
        }

        if (parentItem && parentItem.columnFillingLeft > item.columnFillingLeft) {
          item.columnFillingLeft = parentItem.columnFillingLeft;
        }

        const itemTotalColumnFilling = item.columnFilling + item.columnFillingLeft;

        maxFilling = maxFilling > itemTotalColumnFilling ? maxFilling : itemTotalColumnFilling;
      }
    },
  );

  return { maxFilling };
};

export const mapFlatHierarchy = (hierarchy, nodes, limit = 10) => {
  const mappedNodes = mapByKey(nodes, 'id');
  const sourcesByJunctions = mapSourcesByJunction(nodes);

  let flatHierarchy = { items: [] };

  if (hierarchy.id) {
    appendNodeToHierarchy(
      flatHierarchy.items,
      hierarchy,
      mappedNodes,
      0,
      null,
      sourcesByJunctions,
    );
  }

  flatHierarchy = {
    ...flatHierarchy,
    ...calculateHierarchyColumnFilling(flatHierarchy.items, hierarchy, sourcesByJunctions, limit),
  };

  return flatHierarchy;
};

export const calculateNodeX = ({ columnFilling, columnFillingLeft }) => {
  const fillingWidthLeft = (COLUMN_WIDTH + COLUMN_GAP) * columnFillingLeft;
  let marginLeft = 0;

  if (columnFilling > 1) {
    const fillingWidth = (COLUMN_WIDTH + COLUMN_GAP) * columnFilling;

    marginLeft = fillingWidth / 2 - COLUMN_WIDTH / 2;
  }

  return fillingWidthLeft + marginLeft + SCHEMA_PADDING_LEFT;
};

const calculateNodeY = (item, parentNode) => {
  if (parentNode) {
    return parentNode.positionY + parentNode.height + NODE_MARGIN_TOP;
  }

  return SCHEMA_PADDING_TOP;
};

export const calculateNodesPosition = (nodes, hierarchy, display = {}, limit = 10) => {
  const positionedNodes = [];
  const { items } = mapFlatHierarchy(hierarchy, nodes, limit);

  let rightBorder = 0;
  let countChargers = 0;
  const chargers = [];

  const pushToPositionedNodes = (item, positionX, positionY) => {
    positionedNodes.push({ ...item.node, positionX, positionY });

    if (item.node.width) {
      if (rightBorder < positionX + item.node.width) {
        rightBorder = positionX + item.node.width;
      }
    }
  };

  moveThroughFlatHierarchyItems(
    items,
    ({ item }) => {
      if (!item.node) {
        return;
      }

      let positionX = calculateNodeX(item);
      let positionY = calculateNodeY(item, positionedNodes.find((node) => node.id === item.parent));

      if (item.node.topologyNodeType === 'CHARGER') {
        if (countChargers >= limit) {
          const parent = chargers[countChargers - limit];

          if (parent) {
            positionX = parent.positionX;
            positionY = parent.positionY + parent.height + NODE_MARGIN_TOP * 2;
            pushToPositionedNodes(item, positionX, positionY);
            chargers.push({ ...item.node, positionX, positionY });
          }
        } else {
          pushToPositionedNodes(item, positionX, positionY);
          chargers.push({ ...item.node, positionX, positionY });
        }
        countChargers++;
      } else {
        pushToPositionedNodes(item, positionX, positionY);
      }
    },
  );

  const schemaWidth = rightBorder - SCHEMA_PADDING_LEFT;

  if (schemaWidth < display.width) {
    const extraMargin = (display.width - schemaWidth) / 2 - SCHEMA_PADDING_LEFT;

    return positionedNodes.map((node) => ({ ...node, positionX: node.positionX + extraMargin }));
  }

  return positionedNodes;
};

export const mapHierarchy = (nodes, hierarchy) => {
  const mappedNodes = [];
  const { items } = mapFlatHierarchy(hierarchy, nodes);

  moveThroughFlatHierarchyItems(
    items,
    ({ item }) => mappedNodes.push({ ...item.node, children: item.children, parent: item.parent }),
  );

  return mappedNodes;
};

export const detectEnergyDirection = (node) => {
  const { energyDirection, presentLoad, online } = node;

  const isOnlineChargerOrAnotherNode = node.topologyNodeType !== CHARGER_NODE_TYPE || online;

  // 'IN' energy direction hack for all sessions "charging" state
  return (
    energyDirection || (
      isNumber(presentLoad) && isOnlineChargerOrAnotherNode && presentLoad !== 0 ? 'IN' : null
    )
  );
};

export const makeConnection = (node, parentNode, params = {}) => {
  const {
    energyDirection = detectEnergyDirection(node),
    extendStartX = 0, extendStartY = 0,
    extendEndX = 0, extendEndY = 0,
    tween = false,
  } = params;

  return {
    sourceId: parentNode.id,
    start: {
      x: parentNode.positionX + parentNode.width / 2 + extendStartX,
      y: parentNode.positionY + parentNode.height + extendStartY,
    },
    targetId: node.id,
    end: {
      x: node.positionX + node.width / 2 + extendEndX,
      y: node.positionY + extendEndY,
    },
    energy: {
      direction: energyDirection,
      isSourceNode: isExternalSourceNode(node),
    },
    tween,
  };
};

export const makeConnections = (nodes, hierarchy) => {
  const connections = [];
  const mappedNodes = mapByKey(nodes, 'id');
  const flatHierarchy = mapFlatHierarchy(hierarchy, nodes);
  const energyDirectionByChildren = {};

  moveThroughFlatHierarchyItems(flatHierarchy.items.reverse(), ({ item }) => {
    const { node, parent } = item;

    if (node) {
      const { id } = node;
      const parentNode = mappedNodes[parent];

      if (parentNode) {
        const energyDirection = detectEnergyDirection(node);

        if (!energyDirectionByChildren[parent] && node.topologyNodeType !== 'JUNCTION') {
          energyDirectionByChildren[parent] = [];
        }

        if (!energyDirectionByChildren[id]) {
          connections.push(makeConnection(node, parentNode));

          if (node.topologyNodeType !== JUNCTION_NODE_TYPE) { energyDirectionByChildren[parent].push(energyDirection); }
        } else {
          const uniqDirections = uniq(energyDirectionByChildren[id]);
          let thereIsOne = false;

          if (uniqDirections.includes('IN')) {
            connections.push(makeConnection(node, parentNode, { energyDirection: 'IN' }));
            if (node.topologyNodeType !== JUNCTION_NODE_TYPE) { energyDirectionByChildren[parent].push('IN'); }
            thereIsOne = true;
          }

          if (uniqDirections.includes('OUT') && node.topologyNodeType !== JUNCTION_NODE_TYPE) {
            connections.push(makeConnection(
              node,
              parentNode,
              {
                energyDirection: 'OUT',
                extendStartX: -2,
                extendStartY: 2,
                extendEndX: -2,
                extendEndY: 2,
                tween: thereIsOne,
              },
            ));

            energyDirectionByChildren[parent].push('OUT');
            thereIsOne = true;
          }

          if (!thereIsOne) {
            connections.push(
              makeConnection(node, parentNode, { energyDirection: null }),
            );
          }
        }
      }
    }
  });

  return connections;
};

export const levelWidth = (levelCount) => levelCount * (COLUMN_WIDTH + COLUMN_GAP);

export const columnMargin = (maxOnLevels, itemCount) => (levelWidth(maxOnLevels) - levelWidth(itemCount)) / 2;

export const calculateSchemaWidth = (columnCount) => columnCount * (COLUMN_WIDTH + COLUMN_GAP) + SCHEMA_PADDING_LEFT + SCHEMA_PADDING_RIGHT;

export const getExtremePosition = (nodes) => {
  let x = 0; let
    y = 0;

  nodes.forEach(
    (node) => {
      if (node.staticSection.positionX > x) x = node.staticSection.positionX;
      if (node.staticSection.positionY > y) y = node.staticSection.positionY;
    },
  );

  return { x, y };
};

export const schemaTotalHeight = (extremePosition) => {
  const availableHeight = window.innerHeight - MENU_HEIGHT;
  const schemaHeight = extremePosition.y + 500;

  return schemaHeight > availableHeight ? schemaHeight : availableHeight;
};

export const schemaTotalWidth = (extremePosition) => {
  const screenWidth = window.innerWidth - EVENT_PANEL_WIDTH;

  const schemaWidth = extremePosition.x + 500;

  return schemaWidth > screenWidth ? schemaWidth : screenWidth;
};

const getAngles = (a, b, c) => {
  const { acos, PI } = Math;

  return {
    a: acos((b ** 2 + c ** 2 - a ** 2) / (2 * b * c)) * 180 / PI,
    b: acos((a ** 2 + c ** 2 - b ** 2) / (2 * a * c)) * 180 / PI,
    c: acos((a ** 2 + b ** 2 - c ** 2) / (2 * b * a)) * 180 / PI,
  };
};

export const calculateEnergyDirectionArrowProps = (connection) => {
  const {
    start,
    end,
    energy: { direction, isSourceNode },
  } = connection;
  const { sqrt, abs } = Math;

  let x; let y; let
    angle;

  const hypotenuseLength = sqrt((end.x - start.x) ** 2 + (end.y - start.y) ** 2);
  const adjacentLegLength = abs(end.y - start.y);
  const oppositeLegLength = abs(end.x - start.x);
  const angles = getAngles(oppositeLegLength, adjacentLegLength, hypotenuseLength);

  // Straight horizontal line
  if (start.x === end.x) {
    if (start.y < end.y) {
      x = start.x;
      y = start.y + ((end.y - start.y) / 2 - 15);
      angle = 0;
    } else {
      x = start.x;
      y = start.y + ((end.y - start.y) / 2) + 5;
      angle = 180;
    }
  } else {
    // Straight vertical line
    if (start.y === end.y) {
      if (start.x < end.x) {
        x = start.x + ((end.x - start.x) / 2 - 15);
        y = start.y;
        angle = -90;
      } else {
        x = start.x + ((end.x - start.x) / 2 + 10);
        y = start.y;
        angle = 90;
      }
    } else if (end.x > start.x) {
      // Direction from TOP-LEFT to BOTTOM-RIGHT
      if (end.y > start.y) {
        angle = -angles.a;
        x = abs((end.x - start.x) / 2) + start.x;
        y = abs((end.y - start.y) / 2) + start.y;
      } else {
        // Direction from BOTTOM-LEFT to TOP-RIGHT
        angle = angles.a - 180;
        x = abs((end.x - start.x) / 2) + start.x;
        y = start.y - abs((end.y - start.y) / 2);
      }
    } else {
      // Direction from TOP-RIGHT to BOTTOM-LEFT
      if (end.y > start.y) {
        angle = angles.a;
        x = start.x - abs((end.x - start.x) / 2);
        y = abs((end.y - start.y) / 2) + start.y;
      } else {
        // Direction from BOTTOM-RIGHT to TOP-LEFT
        angle = -angles.a - 180;
        x = start.x - abs((end.x - start.x) / 2);
        y = start.y - abs((end.y - start.y) / 2);
      }
    }
  }

  angle += direction === 'OUT' ? 180 : 0;
  angle += isSourceNode ? -180 : 0;

  return { x, y, angle };
};

export const renderConnection = (connection, props = {}) => {
  const {
    showEnergyFlow = false, hideRemoveTrigger = false, mode, hiddenNode,
  } = props;
  const {
    start, end, tween, sourceId, targetId,
  } = connection;

  const skipRendering = tween || (hiddenNode && (hiddenNode.node.id === sourceId || hiddenNode.node.id === targetId));

  return skipRendering ? null : (
    <svg key={`${start.x}-${start.y}-${end.x}-${end.y}`}>
      <ConnectionLine
        connection={connection}
        showEnergyFlow={showEnergyFlow && mode !== EDIT_SCHEMA_MODE}
      />
      {!hideRemoveTrigger && <RemoveConnectionTrigger connection={connection} />}
      <EnergyDirectionArrow
        connection={connection}
        showEnergyFlow={showEnergyFlow && mode !== EDIT_SCHEMA_MODE}
      />
    </svg>
  );
};
